import React, { FC, useEffect, useState, useCallback, useRef } from 'react';
import styles from './PowerBIEmbedded.module.scss';
import { Embed, models, service } from 'powerbi-client';
import { PowerBIEmbed } from 'powerbi-client-react';
import Spinner from 'components/atoms/Custom/Spinner/Spinner';
import { BooleanCallback } from 'types/Callbacks';
import { useClassNameBuilderFactory } from 'common/providers/ReactKitProvider';
import { usePowerBITracking } from '../../../../hooks/usePageTracking';
import { POWERBI_RESET_TO_DEFAULT_EVENT } from '../../../../common/helpers/home-navigation-helper';

interface IPowerBIEmbeddedProps {
	reportClass?: string;
	embedUrl: string;
	accessToken: string;
	loading: boolean;
	onRenderedPowerBI: BooleanCallback;
	layoutType?: models.LayoutType;
	menuName?: string;
}

const PowerBIEmbedded: FC<IPowerBIEmbeddedProps> = ({
	reportClass,
	embedUrl,
	accessToken,
	loading,
	onRenderedPowerBI,
	layoutType = models.LayoutType.Master,
	menuName = 'Dashboard',
}) => {
	const [isLoading, setIsLoading] = useState<boolean>(false);
	const [currentReport, setCurrentReport] = useState<Embed | null>(null);
	const [currentPageDisplayName, setCurrentPageDisplayName] = useState<string>('');
	const { trackPowerBIPage, trackPowerBIEvent } = usePowerBITracking(menuName);
	
	const currentReportRef = useRef<Embed | null>(null);
	const trackPowerBIEventRef = useRef(trackPowerBIEvent);
	const currentPageDisplayNameRef = useRef(currentPageDisplayName);
	const trackPowerBIPageRef = useRef(trackPowerBIPage);
	
	useEffect(() => {
		currentReportRef.current = currentReport;
	}, [currentReport]);

	useEffect(() => {
		trackPowerBIEventRef.current = trackPowerBIEvent;
		trackPowerBIPageRef.current = trackPowerBIPage;
	}, [trackPowerBIEvent, trackPowerBIPage]);
	
	useEffect(() => {
		currentPageDisplayNameRef.current = currentPageDisplayName;
	}, [currentPageDisplayName]);
	
	const [embeddedReportConfig, setEmbeddedReportConfig] = useState<models.IReportEmbedConfiguration>({
		type: 'report',
		embedUrl: undefined,
		tokenType: models.TokenType.Embed,
		accessToken: undefined,
		settings: {
			layoutType: layoutType,
			customLayout: {
				displayOption: models.DisplayOption.FitToWidth,
				reportAlignment: models.ReportAlignment.Center,
			},
			navContentPaneEnabled: false,
		},
	});

	// SIMPLE: Navigate to page from URL
	const navigateToPageFromURL = async () => {
		if (!currentReport) return;
		
		const urlParams = new URLSearchParams(window.location.search);
		const pageFromUrl = urlParams.get('page');
		
		if (pageFromUrl) {
			await navigateToSpecificPage(pageFromUrl);
		}
	};

	// Navigate to a specific Power BI page
	const navigateToSpecificPage = async (pageId: string) => {
		if (!currentReport) return;
		
		try {
			const pages = await (currentReport as any).getPages();
			const targetPage = pages.find((page: any) => page.name === pageId);
			if (targetPage) {
				await targetPage.setActive();
			}
		} catch (error) {
			console.error('navigation error:', error);
		}
	};

	const handleVisualClicked = useCallback((event?: service.ICustomEvent<any>) => {
		if (event?.detail) {
			const visual = event.detail;
			const visualName = visual.visual?.title || visual.visual?.name || visual.visualName || visual.name || 'Unknown';
			const visualType = visual.visual?.type || visual.visualType || visual.type || 'Unknown';
			trackPowerBIEventRef.current('PBI Visual Clicked', currentPageDisplayNameRef.current, visualName, visualType);
		}
	}, []);

	const handleDataSelected = useCallback((event?: service.ICustomEvent<any>) => {
		if (event?.detail) {
			const selection = event.detail;
			const visualName = selection.visual?.title || selection.visual?.name || selection.visualName || selection.name || 'Unknown';
			const visualType = selection.visual?.type || selection.visualType || selection.type || 'Unknown';
			const selectionData = JSON.stringify(selection.dataPoints || selection);
			trackPowerBIEventRef.current('PBI Selection Changed', currentPageDisplayNameRef.current, visualName, visualType, undefined, selectionData);
		}
	}, []);

	const handleFiltersApplied = useCallback((event?: service.ICustomEvent<any>) => {
		if (event?.detail) {
			const filters = event.detail;
			const filterData = JSON.stringify(filters.filters || filters);
			trackPowerBIEventRef.current('PBI Filter Changed', currentPageDisplayNameRef.current, undefined, undefined, filterData);
		}
	}, []);

	const handleSlicerStateChanged = useCallback((event?: service.ICustomEvent<any>) => {
		if (event?.detail) {
			const slicer = event.detail;
			const visualName = slicer.visualName || slicer.name || 'Unknown';
			const filterData = JSON.stringify(slicer.state || slicer);
			trackPowerBIEventRef.current('PBI Filter Changed', currentPageDisplayNameRef.current, visualName, 'Slicer', filterData);
		}
	}, []);

	const handlePageChanged = useCallback((event?: service.ICustomEvent<any>) => {
		if (event?.detail?.newPage) {
			const pageId = event.detail.newPage.name;
			const displayName = event.detail.newPage.displayName || '';
			setCurrentPageDisplayName(displayName);
			console.log('event', event);
			const newUrl = `${window.location.origin}${window.location.pathname}?page=${pageId}`;
			window.history.pushState({}, '', newUrl);
			trackPowerBIPageRef.current(pageId, displayName);
		}
	}, []);

	const [eventHandlersMap] = useState<Map<string, (event?: any) => void>>(
		new Map([
			[
				'loaded',
				() => {
					onRenderedPowerBI(false);
					setIsLoading(false);
				},
			],
			[
				'rendered',
				() => {
					setIsLoading(false);
					onRenderedPowerBI(false);
					
					// Navigate to URL page after render
					setTimeout(navigateToPageFromURL, 2000);
				},
			],
			[
				'error',
				(event?: service.ICustomEvent<any>) => {
					if (event) {
						console.error(event.detail);
					}
				},
			],
			['visualClicked', handleVisualClicked],
			['dataSelected', handleDataSelected],
			['filtersApplied', handleFiltersApplied],
			['slicerStateChanged', handleSlicerStateChanged],
			['pageChanged', handlePageChanged],
		])
	);

	// Track initial page when report is loaded
	useEffect(() => {
		if (!currentReport) return;

		const trackInitialPage = async () => {
			try {
				const pages = await (currentReport as any).getPages();
				const activePage = pages.find((page: any) => page.isActive);
				if (activePage && !currentPageDisplayName) {
					const displayName = activePage.displayName || '';
					setCurrentPageDisplayName(displayName);
					trackPowerBIPageRef.current(activePage.name, displayName);
				}
			} catch (error) {
				console.error('Error getting initial page:', error);
			}
		};

		const timer = setTimeout(trackInitialPage, 1000);
		return () => clearTimeout(timer);
	}, [currentReport, currentPageDisplayName, trackPowerBIPage]);

	// Handle browser back/forward
	useEffect(() => {
		const handlePopState = () => {
			setTimeout(navigateToPageFromURL, 500);
		};
		
		// Handle custom Power BI navigation events from favourites
		const handlePowerBINavigate = (event: CustomEvent) => {
			const pageId = event.detail?.pageId;
			if (pageId && currentReport) {
				navigateToSpecificPage(pageId);
			}
		};

		const handlePowerBIResetToDefault = (event: CustomEvent) => {
			const report = currentReportRef.current;
			if (!report) return;

			const pageId = event.detail?.pageId as string | null | undefined;
			(async () => {
				try {
					const pages = await (report as any).getPages();
					if (!pages.length) return;

					if (pageId) {
						const targetPage = pages.find((page: any) => page.name === pageId);
						if (targetPage) {
							await targetPage.setActive();
							return;
						}
					}

					const sorted = [...pages].sort((a: any, b: any) => (a.order ?? 0) - (b.order ?? 0));
					if (sorted[0]) {
						await sorted[0].setActive();
					}
				} catch (error) {
					console.error('reset to default page error:', error);
				}
			})();
		};
		
		window.addEventListener('popstate', handlePopState);
		window.addEventListener('powerbi-navigate', handlePowerBINavigate as (event: Event) => void);
		window.addEventListener(POWERBI_RESET_TO_DEFAULT_EVENT, handlePowerBIResetToDefault as (event: Event) => void);
		
		return () => {
			window.removeEventListener('popstate', handlePopState);
			window.removeEventListener('powerbi-navigate', handlePowerBINavigate as (event: Event) => void);
			window.removeEventListener(POWERBI_RESET_TO_DEFAULT_EVENT, handlePowerBIResetToDefault as (event: Event) => void);
		};
	}, [currentReport]);

	const classNames = useClassNameBuilderFactory()
		.Create()
		.AddConditionalClass(isLoading, styles.embed_container_hide)
		.AddConditionalClass(!isLoading, styles.embed_container_show)
		.GetClassNames();

	useEffect(() => {
		if (embedUrl != null && accessToken != null) {
			setEmbeddedReportConfig({
				...embeddedReportConfig,
				embedUrl: embedUrl,
				accessToken: accessToken,
			});
		}
	}, [embedUrl, accessToken, loading]);

	useEffect(() => {
		if (loading) {
			setIsLoading(loading);
		}
	}, [loading]);

	return (
		<div className={styles.overlay_container}>
			{isLoading && (
				<div className={styles.overlay}>
					<Spinner />
				</div>
			)}
			
			<div style={{ overflow: 'hidden !important', width: '100%', height: '100%' }}>
				<PowerBIEmbed
					embedConfig={embeddedReportConfig}
					eventHandlers={eventHandlersMap}
					cssClassName={reportClass}
					getEmbeddedComponent={(embedObject: Embed) => {
						setCurrentReport(embedObject);
					}}
				/>
			</div>
		</div>
	);
};

export default PowerBIEmbedded;